import React, { useState, useEffect } from 'react';
import { questionBankService } from '../services/questionBankService';
import { QuestionBank, BankQuestion } from '../types';
import { ArrowLeftIcon } from './Icons';

interface ImportFromBankModalProps {
  isOpen: boolean;
  questionnaireId: number;
  onClose: () => void;
  onImported: (count: number) => void;
}

const ImportFromBankModal: React.FC<ImportFromBankModalProps> = ({ isOpen, questionnaireId, onClose, onImported }) => {
  const [banks, setBanks] = useState<QuestionBank[]>([]);
  const [selectedBank, setSelectedBank] = useState<QuestionBank | null>(null);
  const [questions, setQuestions] = useState<BankQuestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchBanks();
      setSelectedBank(null);
      setQuestions([]);
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const data = await questionBankService.getAllBanks();
      setBanks(data);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBank = async (bank: QuestionBank) => {
    try {
      setLoading(true);
      setSelectedBank(bank);
      const data = await questionBankService.getBankQuestions(bank.id);
      setQuestions(data);
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.id)));
    }
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;
    try {
      setImporting(true);
      const ids = Array.from(selectedIds);
      await questionBankService.importFromBank(questionnaireId, ids);
      onImported(ids.length);
    } catch (error) {
      console.error('Failed to import:', error);
    } finally {
      setImporting(false);
    }
  };

  const getQuestionTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      SINGLE_CHOICE: '◉',
      MULTIPLE_CHOICE: '☑',
      TEXT: '⌨',
      SLIDER: '─',
      MATRIX: '▦',
      DESCRIPTION: '📄',
      FILL_IN_BLANK: '___',
      DYNAMIC_FILL_IN_BLANK: '＋___',
    };
    return icons[type] || '?';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedBank && (
              <button
                onClick={() => { setSelectedBank(null); setQuestions([]); setSelectedIds(new Set()); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeftIcon size={20} />
              </button>
            )}
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedBank ? `${selectedBank.name} - 选择题目` : '从题库导入'}
            </h3>
          </div>
          {selectedBank && questions.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.size === questions.length}
                onChange={toggleAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              全选
            </label>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">加载中...</div>
          ) : !selectedBank ? (
            // Bank list
            banks.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                暂无题库，请先在题库管理中创建题库
              </div>
            ) : (
              <div className="space-y-2">
                {banks.map((bank) => (
                  <button
                    key={bank.id}
                    onClick={() => handleSelectBank(bank)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                  >
                    <div className="font-medium text-gray-900">{bank.name}</div>
                    {bank.description && (
                      <div className="text-sm text-gray-500 mt-0.5 line-clamp-1">{bank.description}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">{bank.questionCount} 道题目</div>
                  </button>
                ))}
              </div>
            )
          ) : (
            // Question list
            questions.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                该题库暂无题目
              </div>
            ) : (
              <div className="space-y-2">
                {questions.map((question, index) => (
                  <label
                    key={question.id}
                    className={`flex items-start gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                      selectedIds.has(question.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(question.id)}
                      onChange={() => toggleSelection(question.id)}
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{getQuestionTypeIcon(question.type)}</span>
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {question.type === 'DESCRIPTION' ? '说明文本' : question.title}
                        </span>
                        <span className="flex-shrink-0 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                          {question.typeDisplayName}
                        </span>
                      </div>
                      {(question.type === 'SINGLE_CHOICE' || question.type === 'MULTIPLE_CHOICE') && question.options.length > 0 && (
                        <div className="mt-1 text-xs text-gray-500">
                          {question.options.slice(0, 4).map((opt, i) => (
                            <span key={opt.id}>
                              {i > 0 && ' | '}
                              {String.fromCharCode(65 + i)}. {opt.content}
                            </span>
                          ))}
                          {question.options.length > 4 && <span> ... 共{question.options.length}个选项</span>}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {selectedBank && selectedIds.size > 0
              ? `已选择 ${selectedIds.size} 道题目`
              : selectedBank ? '请选择要导入的题目' : '请选择题库'
            }
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
            {selectedBank && (
              <button
                onClick={handleImport}
                disabled={selectedIds.size === 0 || importing}
                className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {importing ? '导入中...' : `导入 ${selectedIds.size} 道题目`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportFromBankModal;
